import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AppShell from "@/components/AppShell";
import PageHeader from "@/components/PageHeader";
import BrokerOAuthWizard from "@/components/BrokerOAuthWizard";
import BrokerTokenTimes from "@/components/BrokerTokenTimes";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Plug, Loader2, ShieldCheck, ExternalLink, Trash2, KeyRound, RefreshCw, Check, Wand2, Copy, Globe, ToggleLeft, ToggleRight, Pencil, Lock } from "lucide-react";
import { toast } from "sonner";

export default function Brokers() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null); // broker being edited (manual)
  const [wizardBroker, setWizardBroker] = useState(null);
  const [form, setForm] = useState({});
  const [editingFields, setEditingFields] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [relinkBusy, setRelinkBusy] = useState(false);
  const [kiteLoginUrl, setKiteLoginUrl] = useState(null);
  const [kiteLoginLoading, setKiteLoginLoading] = useState(false);
  const [egressIp, setEgressIp] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/brokers");
      setItems(data.items);
    } catch {
      toast.error("Could not load brokers");
    }
  };

  useEffect(() => {
    load();
    // Defer egress IP fetch outside the effect tick so React 19's
    // set-state-in-effect rule doesn't fire on the .then callback.
    setTimeout(() => {
      api
        .get("/system/egress-ip")
        .then((res) => setEgressIp(res.data.ip))
        .catch(() => undefined);
    }, 0);
  }, []);

  const openConnect = (broker) => {
    setActive(broker);
    setEditingFields(new Set());
    setKiteLoginUrl(null);
    setKiteLoginLoading(false);
    const savedSet = new Set(broker.saved_fields || []);
    setForm(Object.fromEntries(
      broker.fields.map((f) => [f.key, savedSet.has(f.key) ? "__saved__" : ""])
    ));
  };

  const openKiteLogin = async () => {
    // If api_key is typed in the form, build the URL client-side (no round-trip needed).
    const typedKey = form.api_key && form.api_key !== "__saved__" ? form.api_key : null;
    if (typedKey) {
      window.open(`https://kite.trade/connect/login?api_key=${typedKey}&v=3`, "_blank", "width=600,height=700,noopener,noreferrer");
      return;
    }
    // api_key is saved server-side — fetch it lazily only when the user clicks.
    setKiteLoginLoading(true);
    try {
      const { data } = await api.get("/brokers/zerodha/login-url");
      setKiteLoginUrl(data.login_url);
      window.open(data.login_url, "_blank", "width=600,height=700,noopener,noreferrer");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not get Kite login URL — save your API Key first");
    } finally {
      setKiteLoginLoading(false);
    }
  };

  const toggleFieldEdit = (key) => {
    setEditingFields((prev) => {
      const next = new Set(prev);
      if (next.has(key)) { next.delete(key); setForm((f) => ({ ...f, [key]: "__saved__" })); }
      else { next.add(key); setForm((f) => ({ ...f, [key]: "" })); }
      return next;
    });
  };

  const saveConnect = async () => {
    if (!active) return;
    setBusy(true);
    try {
      await api.post(`/brokers/${active.name}/connect`, { credentials: form });
      toast.success(`${active.label} credentials saved (encrypted)`);
      const { data } = await api.post(`/brokers/${active.name}/test`);
      if (data.status === "live") toast.success(`${active.label} live: ${data.message}`);
      else toast.warning(`${active.label} saved but test failed: ${data.message}`);
      setActive(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const testNow = async (name) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/brokers/${name}/test`);
      if (data.status === "live") toast.success(`Connected: ${data.message}`);
      else toast.warning(`Test failed: ${data.message}`);
      load();
    } catch {
      toast.error("Test failed");
    } finally {
      setBusy(false);
    }
  };

  const relink = async (name) => {
    setRelinkBusy(true);
    try {
      const { data } = await api.post(`/brokers/${name}/oauth/relink`);
      window.open(data.login_url, "_blank", "width=600,height=700,noopener,noreferrer");
      // Poll until connection status flips back to live (or 5 min timeout).
      const started = Date.now();
      const t = setInterval(async () => {
        if (Date.now() - started > 5 * 60 * 1000) { clearInterval(t); setRelinkBusy(false); return; }
        try {
          const { data: br } = await api.get("/brokers");
          const me = br.items.find((b) => b.name === name);
          if (me?.status === "live") {
            clearInterval(t);
            setRelinkBusy(false);
            toast.success(`${name.toUpperCase()} relinked — access token refreshed`);
            load();
          }
        } catch { /* keep polling */ }
      }, 2500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Relink failed");
      setRelinkBusy(false);
    }
  };

  // RU-BRKDISC: disconnect is confirmed first (BD-2); a refusal's own text is shown unchanged (BD-3).
  const [discTarget, setDiscTarget] = useState(null); // broker card awaiting confirmation
  const [discBusy, setDiscBusy] = useState(false);
  const [discError, setDiscError] = useState(null);

  const askDisconnect = (broker) => {
    setDiscError(null);
    setDiscTarget(broker);
  };

  const closeDisconnect = () => {
    if (discBusy) return;
    setDiscTarget(null);
    setDiscError(null);
  };

  const confirmDisconnect = async () => {
    if (!discTarget || discBusy) return;
    const { name, label } = discTarget;
    setDiscBusy(true);
    setDiscError(null);
    try {
      await api.delete(`/brokers/${name}`);
      toast.success(`${label || name} disconnected: stored credentials deleted`);
      setDiscTarget(null);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" && detail
        ? detail
        : `Disconnect of ${label || name} failed${e?.response?.status ? ` (HTTP ${e.response.status})` : ""}`;
      setDiscError(msg);
      toast.error(msg);
    } finally {
      setDiscBusy(false);
    }
  };

  const toggleIpMode = async (name, currentDedicated) => {
    setBusy(true);
    try {
      await api.post(`/brokers/${name}/ip-mode`, { dedicated: !currentDedicated });
      toast.success(!currentDedicated ? "Dedicated IP assigned" : "Switched to VPS egress IP");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to change IP mode");
    } finally {
      setBusy(false);
    }
  };

  const reconcile = async (name) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/reconciliation/run/${name}`);
      if (data.state === "NOT_APPLICABLE") {
        toast.info(`${name}: paper broker (no reconciliation needed)`);
      } else if (data.state === "PENDING_RECONCILE") {
        toast.warning(`${name}: ${data.note || "awaiting live adapter"}`);
      } else if (data.state === "FAILED") {
        toast.error(`${name}: reconcile failed — ${data.error}`);
      } else {
        toast.success(`${name}: ${data.checked} orders checked, ${data.actions.length} actions`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reconcile failed");
    } finally {
      setBusy(false);
    }
  };

  const CAP_LABELS = {
    supports_modify: "modify",
    supports_amo: "AMO",
    supports_iceberg: "iceberg",
    supports_basket_native: "native basket",
    supports_postback_ws: "WS postback",
    supports_options_multi_leg: "multi-leg",
  };

  const statusLabel = (it) => {
    if (!it.connected) return { label: "DISCONNECTED", color: "text-zinc-500", dot: "bg-zinc-600" };
    if (it.status === "live") return { label: "LIVE", color: "txt-profit", dot: "bg-emerald-500" };
    if (it.status === "error") return { label: "ERROR", color: "txt-loss", dot: "bg-red-500" };
    return { label: "SAVED · NOT TESTED", color: "txt-warn", dot: "bg-amber-500" };
  };

  return (
    <AppShell>
      <PageHeader
        overline="Connectivity"
        title="Broker connections"
        description="Wire your live trading account. Credentials are encrypted at rest. Until tested live, all orders stay in the paper engine."
      />

      <div className="p-8">
        <div
          data-testid="brokers-egress-ip-banner"
          className="mb-6 border border-[var(--border)] bg-[var(--bg-surface-2)] p-4 flex items-start gap-3 text-xs"
        >
          <Globe className="w-4 h-4 txt-secondary mt-0.5 shrink-0" />
          <div className="flex-1 txt-secondary">
            <div className="overline text-[var(--text-primary)] mb-1">SEBI Static IP Compliance</div>
            Each broker connection is assigned a <strong className="text-[var(--text-primary)]">unique dedicated IP</strong> —
            IPv6 where the broker supports it, IPv4 otherwise. Your assigned IP is shown on each
            connected broker card below. Whitelist only that IP in your broker&apos;s developer console.
            {egressIp && (
              <span className="ml-1">
                Server egress fallback:{" "}
                <span className="font-mono text-[var(--text-primary)] bg-zinc-900 px-1.5 py-0.5 border border-zinc-700">
                  {egressIp}
                </span>
              </span>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {items.map((b) => {
            const st = statusLabel(b);
            return (
              <div key={b.name} className="panel p-5 flex flex-col gap-4" data-testid={`broker-card-${b.name}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-display text-2xl">{b.label}</div>
                    <p className="text-sm txt-secondary mt-1">{b.description}</p>
                    <div className="flex items-center gap-2 mt-3">
                      <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                      <span className={`overline ${st.color}`}>{st.label}</span>
                      {b.last_test && <span className="overline txt-muted">last test {b.last_test.slice(11, 19)}</span>}
                    </div>
                    {b.connected && <BrokerTokenTimes broker={b} />}
                  </div>
                  <KeyRound className="w-5 h-5 txt-muted shrink-0" />
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-auto">
                  <Button
                    data-testid={`broker-wizard-${b.name}`}
                    onClick={() => setWizardBroker(b)}
                    size="sm"
                    variant="outline"
                    className="rounded-none border-amber-500 text-amber-400 hover:bg-amber-500 hover:text-black font-section tracking-wider"
                  >
                    <Wand2 className="w-3.5 h-3.5 mr-2" /> WIZARD
                  </Button>
                  <Button
                    data-testid={`broker-connect-${b.name}`}
                    onClick={() => openConnect(b)}
                    size="sm"
                    className="rounded-none bg-white text-black hover:bg-zinc-200 font-section tracking-wider"
                  >
                    <Plug className="w-3.5 h-3.5 mr-2" /> {b.connected ? "UPDATE" : "CONNECT"}
                  </Button>
                  {b.connected && (
                    <>
                      <Button
                        data-testid={`broker-test-${b.name}`}
                        onClick={() => testNow(b.name)}
                        disabled={busy}
                        size="sm"
                        variant="outline"
                        className="rounded-none border-white text-white hover:bg-white hover:text-black"
                      >
                        <ShieldCheck className="w-3.5 h-3.5 mr-2" /> TEST
                      </Button>
                      {(b.name === "zerodha" || b.name === "upstox") && (
                        <Button
                          data-testid={`broker-relink-${b.name}`}
                          onClick={() => relink(b.name)}
                          disabled={relinkBusy}
                          size="sm"
                          variant="outline"
                          className="rounded-none border-amber-500 text-amber-400 hover:bg-amber-500 hover:text-black"
                          title="Refresh daily access token (Kite/Upstox tokens expire ~6 AM IST)"
                        >
                          {relinkBusy
                            ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> LINKING…</>
                            : <><RefreshCw className="w-3.5 h-3.5 mr-1" /> RELINK</>}
                        </Button>
                      )}
                      <Button
                        data-testid={`broker-reconcile-${b.name}`}
                        onClick={() => reconcile(b.name)}
                        disabled={busy}
                        size="sm"
                        variant="ghost"
                        className="rounded-none text-zinc-400 hover:text-white"
                        title="Reconcile order book with broker"
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        data-testid={`broker-disconnect-${b.name}`}
                        onClick={() => askDisconnect(b)}
                        size="sm"
                        variant="outline"
                        className="rounded-none border-red-500/60 text-red-400 hover:bg-red-500 hover:text-black font-section tracking-wider"
                        title={`Disconnect ${b.label}: delete its stored credentials`}
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-2" /> DISCONNECT
                      </Button>
                    </>
                  )}
                  <a
                    href={b.docs_url}
                    target="_blank"
                    rel="noreferrer"
                    className="ml-auto text-xs txt-muted hover:text-white inline-flex items-center gap-1"
                    data-testid={`broker-docs-${b.name}`}
                  >
                    Docs <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                {b.capabilities && Object.values(b.capabilities).some(Boolean) && (
                  <div className="border-t border-[var(--border)] pt-3 flex flex-wrap gap-1.5">
                    {Object.entries(CAP_LABELS).map(([k, label]) => (
                      b.capabilities[k] ? (
                        <span key={k} className="text-[10px] uppercase tracking-wider px-2 py-0.5 border border-[var(--border)] txt-secondary font-mono-data inline-flex items-center gap-1">
                          <Check className="w-3 h-3 txt-profit" /> {label}
                        </span>
                      ) : null
                    ))}
                  </div>
                )}
                {b.connected && (
                  <div className="border-t border-[var(--border)] pt-3 space-y-2">
                    {/* Admin IP mode toggle */}
                    {isAdmin && (
                      <div className="flex items-center gap-2">
                        <span className="overline txt-muted">IP Mode</span>
                        <button
                          onClick={() => toggleIpMode(b.name, b.dedicated_ip_mode)}
                          disabled={busy}
                          className="flex items-center gap-1.5 text-[11px] txt-secondary hover:text-[var(--text-primary)] transition-colors"
                          title={b.dedicated_ip_mode ? "Switch to VPS egress IP" : "Switch to dedicated IP"}
                        >
                          {b.dedicated_ip_mode
                            ? <ToggleRight className="w-4 h-4 text-emerald-400" />
                            : <ToggleLeft className="w-4 h-4 txt-muted" />}
                          <span>{b.dedicated_ip_mode ? "Dedicated IP" : "VPS Egress IP"}</span>
                        </button>
                      </div>
                    )}
                    {/* IP display */}
                    {b.assigned_ip ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="overline txt-muted">API IP</span>
                        <span
                          className={`font-mono text-[11px] bg-zinc-900 px-2 py-0.5 border border-zinc-700 ${b.ip_version === 6 ? "text-blue-300" : "text-amber-300"}`}
                          title={b.ip_instructions}
                        >
                          {b.assigned_ip}
                        </span>
                        <span className="text-[10px] border border-[var(--border)] px-1.5 py-0.5 txt-muted uppercase tracking-wider">
                          IPv{b.ip_version}
                        </span>
                        <button
                          onClick={() => { navigator.clipboard.writeText(b.assigned_ip); toast.success("IP copied — whitelist this in your broker console"); }}
                          className="p-1 txt-muted hover:text-[var(--text-primary)] transition-colors"
                          title="Copy IP"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                        <span className="text-[10px] txt-muted truncate max-w-xs" title={b.ip_instructions}>
                          {b.ip_instructions}
                        </span>
                      </div>
                    ) : isAdmin && egressIp ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="overline txt-muted">API IP</span>
                        <span className="font-mono text-[11px] text-amber-300 bg-zinc-900 px-2 py-0.5 border border-zinc-700">
                          {egressIp}
                        </span>
                        <span className="text-[10px] border border-[var(--border)] px-1.5 py-0.5 txt-muted uppercase tracking-wider">
                          VPS
                        </span>
                        <button
                          onClick={() => { navigator.clipboard.writeText(egressIp); toast.success("VPS IP copied"); }}
                          className="p-1 txt-muted hover:text-[var(--text-primary)] transition-colors"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : (
                      <div className="text-[11px] txt-muted">
                        No IP assigned — configure IPV6_PREFIX on the server.
                      </div>
                    )}
                  </div>
                )}
                {b.sdk_package && (
                  <div className="text-[11px] txt-muted font-mono-data border-t border-[var(--border)] pt-2">
                    SDK: {b.sdk_package}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent className="rounded-none bg-[var(--bg-surface)] border-[var(--border)] text-white max-w-md" data-testid="broker-connect-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{active?.label}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {/* Zerodha: step-by-step guide */}
            {active?.name === "zerodha" && (
              <div className="border border-amber-500/30 bg-amber-500/5 p-3 space-y-2 text-xs">
                <p className="font-section text-amber-400">Daily login required (access token expires ~6 AM IST)</p>
                <ol className="txt-secondary space-y-1 list-decimal list-inside">
                  <li>Click <strong className="text-white">Open Kite Login</strong> below to open the Zerodha login page</li>
                  <li>Log in with your Zerodha credentials</li>
                  <li>After redirect, copy the <code className="bg-zinc-800 px-1">request_token=</code> value from the URL bar</li>
                  <li>Paste it in the <strong className="text-white">Request Token</strong> field below and click Save</li>
                </ol>
                <button
                  onClick={openKiteLogin}
                  disabled={kiteLoginLoading}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 text-black text-xs font-section rounded-sm hover:bg-amber-400 transition-colors disabled:opacity-50"
                >
                  {kiteLoginLoading
                    ? <><Loader2 className="w-3 h-3 animate-spin" /> Loading…</>
                    : <><ExternalLink className="w-3 h-3" /> Open Kite Login</>}
                </button>
              </div>
            )}
            {active?.fields.map((f) => {
              const isSaved = form[f.key] === "__saved__" && !editingFields.has(f.key);
              return (
                <div key={f.key}>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="overline">{f.label}</Label>
                    {f.persistent && active?.connected && (
                      <span className="flex items-center gap-1 text-xs txt-muted">
                        <Lock className="w-3 h-3" /> permanent
                      </span>
                    )}
                  </div>
                  {isSaved ? (
                    <div className="flex items-center gap-2 border border-[var(--border)] px-3 py-2 bg-[var(--bg-page)]">
                      <span className="font-mono-data text-sm txt-secondary flex-1">••••••••  saved</span>
                      <button
                        type="button"
                        onClick={() => toggleFieldEdit(f.key)}
                        className="flex items-center gap-1 text-xs txt-muted hover:text-white transition-colors"
                      >
                        <Pencil className="w-3 h-3" /> Edit
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Input
                        data-testid={`broker-field-${f.key}`}
                        type={f.secret ? "password" : "text"}
                        value={form[f.key] || ""}
                        placeholder={f.hint || ""}
                        onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                        className="mt-0 rounded-none bg-[var(--bg-page)] border-[var(--border)] font-mono-data flex-1"
                      />
                      {f.persistent && active?.connected && editingFields.has(f.key) && (
                        <button
                          type="button"
                          onClick={() => toggleFieldEdit(f.key)}
                          className="text-xs txt-muted hover:text-white whitespace-nowrap"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  )}
                  {f.hint && !isSaved && (
                    <p className="text-xs txt-muted mt-0.5">{f.hint}</p>
                  )}
                </div>
              );
            })}
            <p className="text-xs txt-muted pt-2">
              Stored encrypted (Fernet) in your account vault. Only used server-side when you place a live order.
            </p>
          </div>
          <DialogFooter>
            <Button
              data-testid="broker-save-btn"
              onClick={saveConnect}
              disabled={busy}
              className="rounded-none bg-white text-black hover:bg-zinc-200 font-section tracking-wider"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plug className="w-4 h-4 mr-2" />}
              {busy ? "SAVING…" : "SAVE + TEST"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!discTarget} onOpenChange={(v) => !v && closeDisconnect()}>
        <DialogContent className="rounded-none bg-[var(--bg-surface)] border-[var(--border)] text-white max-w-md" data-testid="broker-disconnect-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Disconnect {discTarget?.label || discTarget?.name}?</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm txt-secondary">
            <DialogDescription className="text-sm txt-secondary">
              The stored credentials for <strong className="text-white">{discTarget?.label || discTarget?.name}</strong> are
              deleted from AlgoForge. To use this broker again you must connect it again with its credentials.
            </DialogDescription>
            <p>
              Nothing is placed, cancelled or closed at the broker. AlgoForge refuses the disconnect while a live
              strategy, an open live order or a managed live position still uses this broker.
            </p>
            {Array.isArray(discTarget?.house_roles) && discTarget.house_roles.length > 0 && (
              <div data-testid="broker-disconnect-house-warning" className="border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-300 space-y-1">
                <p>
                  This connection currently supplies, for all users:
                </p>
                <ul className="list-disc list-inside">
                  {discTarget.house_roles.map((r) => <li key={r}>{r}</li>)}
                </ul>
                <p>
                  After disconnecting, the platform switches to another {discTarget?.label || discTarget?.name} connection
                  if one is available, or this data stops.
                </p>
              </div>
            )}
            {discTarget && discTarget.connected && discTarget.house_roles === null && (
              <p data-testid="broker-disconnect-house-unknown" className="text-xs text-amber-300">
                Could not check whether this connection supplies shared market data for all users.
              </p>
            )}
            {discError && (
              <p data-testid="broker-disconnect-error" className="border border-red-500/40 bg-red-500/10 p-2 text-xs text-red-300 whitespace-pre-wrap">
                {discError}
              </p>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button
              data-testid="broker-disconnect-cancel"
              onClick={closeDisconnect}
              disabled={discBusy}
              variant="outline"
              className="rounded-none border-white text-white hover:bg-white hover:text-black"
            >
              CANCEL
            </Button>
            <Button
              data-testid="broker-disconnect-confirm"
              onClick={confirmDisconnect}
              disabled={discBusy}
              className="rounded-none bg-red-500 text-black hover:bg-red-400 font-section tracking-wider"
            >
              {discBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              {discBusy ? "DISCONNECTING…" : "DISCONNECT"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BrokerOAuthWizard
        broker={wizardBroker}
        onClose={() => setWizardBroker(null)}
        onLinked={() => { setWizardBroker(null); load(); }}
      />
    </AppShell>
  );
}
